import client from './client';
import { fetchAllPages } from './fetchAllPages';
import { applyApprovedOnlyParams, applyScopedParams } from './viewerAccess';
import { publishCollectionChange } from '../utils/collectionSync';
import { readRegistryCollection, writeRegistryCollection } from '../component/Admin/data/registryStore';
import { extractCollectionResponse } from './collectionResponse';
import { withOfflineCollectionSupport } from './offlineCollectionSupport';

const DELETED_WORKPLANS_KEY = 'planningDeletedWorkplans';
const DELETED_WORKPLAN_ACTIVITIES_KEY = 'planningDeletedWorkplanActivities';
const DELETED_ACTIVITY_REPORTS_KEY = 'planningDeletedActivityReports';

export const WORKPLAN_PLAN_TYPES = ['Annual', 'Medium Term', 'Emergency'];
export const WORKPLAN_STATUSES = ['Draft', 'Approved', 'Active', 'Completed', 'Suspended', 'Cancelled'];
export const WORKPLAN_APPROVAL_STATUSES = ['Submitted', 'Pending Approval', 'Approved', 'Rejected'];
export const WORKPLAN_AUTH_LEVELS = ['Director', 'DG', 'Minister'];
export const WORKPLAN_GEO_SCOPES = ['National', 'State Specific', 'LGA Targeted'];
export const WORKPLAN_DEPARTMENTS = [
  'Livestock Production',
  'Animal Health',
  'Markets & Trade',
  'Infrastructure',
  'Extension',
  'Planning (DPRS)',
];
export const WORKPLAN_PLAN_CATEGORIES = ['Workplan', 'Reform Plan'];
export const WORKPLAN_BENEFICIARY_CLASSES = ['Farmer', 'Herder', 'Processor', 'Trader', 'Cooperative', 'Women', 'Youth', 'Other'];
export const WORKPLAN_REPORT_TYPES = ['Standard', 'Reform', 'Engagement', 'Coordination', 'Special Intervention', 'PPP Milestone'];

// Reform Plan (DCIC-11, plan_category = reform_plan) — the Reform section
// carries the fields DCIC-25 Reform Plan defined in Field Dictionary v1.1,
// absorbed into the Workplan by the v2.0 rationalisation.
export const REFORM_CATEGORIES = ['Policy Reform', 'Process Improvement', 'Digital Transformation', 'Service Delivery Improvement', 'Organisational Reform'];
export const REFORM_LEAD_DEPARTMENTS = [
  'Livestock Production',
  'Animal Health',
  'Markets & Trade',
  'Infrastructure',
  'Extension',
  'Planning (DPRS)',
  'ICT / M&E',
  'Legal & Regulatory',
  'Finance & Admin',
];
// Reform activity categories (v1.1 activity_category_reform) — offered for
// the repeatable Activity rows when the plan is a Reform Plan.
export const REFORM_ACTIVITY_CATEGORIES = [
  'Policy',
  'Training',
  'Process Review',
  'ICT Upgrade',
  'Capacity Building',
  'Stakeholder Engagement',
  'Monitoring',
  'Evaluation',
  'Communication',
  'Procurement',
  'Other',
];
// Reform Monitoring (DCIC-12, activity_type = reform — ex DCIC-26).
export const REFORM_MONITORING_STATUSES = ['Planned', 'In Progress', 'Completed', 'On Hold', 'Cancelled'];

export const WORKPLAN_ACTIVITY_CATEGORIES = [
  'Training',
  'Field Visit',
  'Survey',
  'Procurement',
  'Meeting',
  'Construction',
  'Campaign',
  'Technical Assistance',
  'Other',
];

export const WORKPLAN_ACTIVITY_STATUSES = ['Planned', 'In Progress', 'Completed', 'Delayed', 'Cancelled'];

export const ACTIVITY_REPORT_STATUSES = ['Completed', 'Partially Completed', 'Not Started', 'Delayed', 'Cancelled'];

// ── Fields the backend does not store yet ─────────────────────────────────
// The v2.0 type selectors and the Reform Plan / Reform Monitoring fields are
// not in the backend's workplan / activity-report schema, so its validation
// drops them. Until the backend adds the columns they are kept per record in
// browser storage and merged back into the record when it is read. Once the
// backend returns a field itself, its value wins.
const EXTENSION_FIELDS = {
  workplans: [
    'plan_category', 'reform_id', 'reform_category', 'supporting_departments',
    'strategic_objective', 'lead_department', 'approval_authority',
  ],
  // v2.2 climate: one flag + one facility link per activity row (DCIC-11) and
  // one link to an approved facility climate assessment per report (DCIC-12).
  'workplan-activities': ['beneficiary_class', 'climate_screening', 'target_facility_id'],
  'activity-reports': [
    'activity_type', 'ppp_id', 'reform_plan_uuid', 'reform_current_status',
    'completed_activity_uuids', 'remarks', 'attachments', 'attachments_file_name',
    'facility_assessment_ref',
  ],
};
const extensionStoreKey = (collectionKey) => `planningExtensions:${collectionKey}`;

function isBlank(value) {
  return value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
}

function readExtensions(collectionKey) {
  const rows = readRegistryCollection(extensionStoreKey(collectionKey));
  return rows && typeof rows === 'object' && !Array.isArray(rows) ? rows : {};
}

function saveExtensions(collectionKey, uuid, builtPayload = {}) {
  const fields = EXTENSION_FIELDS[collectionKey];
  if (!fields || !uuid) return;
  const all = readExtensions(collectionKey);
  const next = { ...(all[uuid] ?? {}) };
  fields.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(builtPayload, field) && builtPayload[field] !== undefined) {
      next[field] = builtPayload[field];
    }
  });
  writeRegistryCollection(extensionStoreKey(collectionKey), { ...all, [uuid]: next });
}

function withExtensions(collectionKey, normalize) {
  return (row = {}) => {
    const uuid = row?.uuid ?? row?.id;
    const stored = uuid ? readExtensions(collectionKey)[uuid] : null;
    if (!stored) return normalize(row);
    const merged = { ...row };
    Object.entries(stored).forEach(([field, value]) => {
      if (isBlank(merged[field])) merged[field] = value;
    });
    return normalize(merged);
  };
}

function trimOrNull(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text ? text : null;
}

function cleanQueryValue(value) {
  if (value === null || value === undefined) return null;
  if (Array.isArray(value)) {
    const parts = value
      .map((entry) => cleanQueryValue(entry))
      .filter((entry) => entry !== null && entry !== undefined && String(entry).trim() !== '');
    return parts.length ? parts.join(',') : null;
  }
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (typeof value === 'number') return Number.isNaN(value) ? null : value;
  const text = String(value).trim();
  return text ? text : null;
}

function buildQueryParams(params = {}, defaults = {}) {
  const query = { ...defaults };

  Object.entries(applyScopedParams(applyApprovedOnlyParams(params))).forEach(([key, value]) => {
    const cleaned = cleanQueryValue(value);
    if (cleaned === null || cleaned === undefined) return;
    query[key] = cleaned;
  });

  return query;
}

function unwrapResponse(responseData) {
  return responseData?.data ?? responseData ?? {};
}

function extractCollection(responseData, normalize) {
  return extractCollectionResponse(responseData, normalize);
}

function extractItem(responseData, normalize, targetUuid, itemKeys = []) {
  const payload = unwrapResponse(responseData);
  const candidates = [payload, payload?.data, payload?.item, payload?.record];

  for (const candidate of candidates) {
    if (!candidate) continue;

    if (Array.isArray(candidate.items)) {
      const match = targetUuid
        ? candidate.items.find((item) => item?.uuid === targetUuid || item?.id === targetUuid)
        : candidate.items[0];

      if (match) return normalize(match);
    }

    if (
      candidate.uuid ||
      candidate.id ||
      itemKeys.some((key) => candidate?.[key] != null)
    ) {
      return normalize(candidate);
    }
  }

  return normalize({});
}

function makeCollectionApi({
  basePath,
  normalize,
  buildPayload,
  defaultPerPage = 25,
  itemKeys = [],
  collectionKey,
}) {
  const syncKey = collectionKey ?? basePath.replace(/^\/v1\//, '').replace(/^\//, '').replace(/\/$/, '');

  async function fetchPage(params = {}) {
    const { data } = await client.get(basePath, {
      params: buildQueryParams(params, {
        per_page: params.per_page ?? defaultPerPage,
        page: params.page ?? 1,
      }),
    });
    return extractCollection(data, normalize);
  }

  return {
    collectionKey: syncKey,

    async list(params = {}) {
      return fetchPage(params);
    },

    async listAll(params = {}) {
      return fetchAllPages((page) => fetchPage({ ...params, page }));
    },

    async get(uuid) {
      const { data } = await client.get(`${basePath}/${uuid}`);
      return extractItem(data, normalize, uuid, itemKeys);
    },

    async create(payload) {
      const built = buildPayload(payload);
      const { data } = await client.post(basePath, built);
      const created = extractItem(data, normalize, null, itemKeys);
      saveExtensions(syncKey, created?.uuid, built);
      const item = created?.uuid ? normalize({ ...(created.raw ?? created) }) : created;
      publishCollectionChange(syncKey, { action: 'create', uuid: item?.uuid ?? null });
      return item;
    },

    async update(uuid, payload) {
      const built = buildPayload(payload);
      saveExtensions(syncKey, uuid, built);
      const { data } = await client.put(`${basePath}/${uuid}`, built);
      const item = extractItem(data, normalize, uuid, itemKeys);
      publishCollectionChange(syncKey, { action: 'update', uuid });
      return item;
    },

    async remove(uuid) {
      const { data } = await client.delete(`${basePath}/${uuid}`);
      publishCollectionChange(syncKey, { action: 'remove', uuid });
      return unwrapResponse(data);
    },

    async submit(uuid) {
      const { data } = await client.post(`${basePath}/${uuid}/submit`);
      const item = extractItem(data, normalize, uuid, itemKeys);
      publishCollectionChange(syncKey, { action: 'submit', uuid });
      return item;
    },

    async validate(uuid) {
      const { data } = await client.post(`${basePath}/${uuid}/validate`);
      const item = extractItem(data, normalize, uuid, itemKeys);
      publishCollectionChange(syncKey, { action: 'validate', uuid });
      return item;
    },

    async approve(uuid) {
      const { data } = await client.post(`${basePath}/${uuid}/approve`);
      const item = extractItem(data, normalize, uuid, itemKeys);
      publishCollectionChange(syncKey, { action: 'approve', uuid });
      return item;
    },

    async reject(uuid, notes = '') {
      if (!notes || !notes.trim()) throw new Error('Rejection reason is required.');
      const { data } = await client.post(`${basePath}/${uuid}/reject`, { notes: notes.trim() });
      const item = extractItem(data, normalize, uuid, itemKeys);
      publishCollectionChange(syncKey, { action: 'reject', uuid });
      return item;
    },
  };
}

function normalizeCode(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_\s-]+/g, '_');
}

function normalizeApiEnum(value) {
  const text = trimOrNull(value);
  return text ? normalizeCode(text) : null;
}

function humanizeCode(value) {
  const text = String(value ?? '').trim();
  if (!text) return '';

  return text
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .replace(/\bLga\b/g, 'LGA')
    .replace(/\bDg\b/g, 'DG')
    .replace(/\bCahw\b/g, 'CAHW')
    .replace(/\bCpd\b/g, 'CPD')
    .replace(/\bNgn\b/g, 'NGN');
}

function firstText(...values) {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    if (typeof value === 'object') continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return '';
}

function personName(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    const text = String(value).trim();
    return text || '';
  }
  if (typeof value === 'object') {
    return firstText(value.name, value.full_name, value.fullName, value.label, value.display_name, value.uuid);
  }
  return '';
}

function parseMoney(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'object') return null;

  const text = String(value).trim();
  if (!text) return null;

  const compact = text.replace(/[\s,₦]/g, '');
  const compactMatch = compact.match(/^(-?\d+(?:\.\d+)?)([kKmMbB])$/);
  if (compactMatch) {
    const [, numberPart, suffix] = compactMatch;
    const base = Number(numberPart);
    if (!Number.isFinite(base)) return null;
    const multiplier =
      suffix.toLowerCase() === 'k' ? 1_000 :
      suffix.toLowerCase() === 'm' ? 1_000_000 :
      1_000_000_000;
    return base * multiplier;
  }

  const numeric = Number(compact.replace(/[^\d.-]/g, ''));
  return Number.isFinite(numeric) ? numeric : null;
}

function formatCurrencyCompact(value) {
  const amount = parseMoney(value);
  if (amount === null) return '';

  const abs = Math.abs(amount);
  let divisor = 1;
  let suffix = '';

  if (abs >= 1_000_000_000) {
    divisor = 1_000_000_000;
    suffix = 'B';
  } else if (abs >= 1_000_000) {
    divisor = 1_000_000;
    suffix = 'M';
  } else if (abs >= 1_000) {
    divisor = 1_000;
    suffix = 'K';
  }

  const scaled = amount / divisor;
  const precision = Math.abs(scaled) >= 100 ? 0 : Math.abs(scaled) >= 10 ? 1 : 2;
  const text = scaled
    .toFixed(precision)
    .replace(/\.0+$/, '')
    .replace(/(\.\d*[1-9])0+$/, '$1');

  return `₦${text}${suffix}`;
}

function normalizeDateValue(value) {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const text = String(value).trim();
  if (!text) return null;

  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const dmyMatch = text.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmyMatch) {
    const [, day, month, year] = dmyMatch;
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const slashMatch = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (slashMatch) {
    const [, day, month, year] = slashMatch;
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateDisplay(value) {
  const date = normalizeDateValue(value);
  if (!date) {
    const text = firstText(value);
    return text || '—';
  }

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatDateInput(value) {
  const date = normalizeDateValue(value);
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function summarizeStructuredValue(value) {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);

  if (Array.isArray(value)) {
    const parts = value.map((item) => summarizeStructuredValue(item)).filter(Boolean);
    return parts.join(', ');
  }

  if (typeof value === 'object') {
    const parts = Object.entries(value)
      .map(([key, entry]) => {
        const text = summarizeStructuredValue(entry);
        return text ? `${humanizeCode(key)}: ${text}` : '';
      })
      .filter(Boolean);
    return parts.join(', ');
  }

  return String(value);
}

function sumNumericValues(value) {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const parsed = parseMoney(value);
    return parsed === null ? 0 : parsed;
  }
  if (Array.isArray(value)) {
    return value.reduce((total, entry) => total + sumNumericValues(entry), 0);
  }
  if (typeof value === 'object') {
    return Object.values(value).reduce((total, entry) => total + sumNumericValues(entry), 0);
  }
  return 0;
}

function parseStructuredField(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'object') return value;

  const text = String(value).trim();
  if (!text) return null;

  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch {
    // fall through to line parsing
  }

  const result = {};
  text
    .split(/[\n;]+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line) => {
      const separatorIndex = line.indexOf(':') >= 0 ? line.indexOf(':') : line.indexOf('=');
      if (separatorIndex <= 0) return;
      const key = line.slice(0, separatorIndex).trim();
      const raw = line.slice(separatorIndex + 1).trim();
      if (!key || !raw) return;
      const numeric = Number(raw.replace(/,/g, ''));
      result[key] = Number.isFinite(numeric) && raw !== '' ? numeric : raw;
    });

  return Object.keys(result).length ? result : text;
}

function normalizeStatusLabel(value) {
  const key = normalizeCode(value);
  if (!key) return '';

  const map = {
    draft: 'Draft',
    submitted: 'Submitted',
    pending_approval: 'Pending Approval',
    pendingapproval: 'Pending Approval',
    approved: 'Approved',
    rejected: 'Rejected',
    active: 'Active',
    completed: 'Completed',
    suspended: 'Suspended',
    cancelled: 'Cancelled',
    canceled: 'Cancelled',
    planned: 'Planned',
    in_progress: 'In Progress',
    delayed: 'Delayed',
    partially_completed: 'Partially Completed',
    not_started: 'Not Started',
    published: 'Published',
  };

  return map[key] ?? humanizeCode(value);
}

function normalizePlanType(value) {
  const key = normalizeCode(value);
  const map = {
    annual: 'Annual',
    medium_term: 'Medium Term',
    mediumterm: 'Medium Term',
    emergency: 'Emergency',
  };
  return map[key] ?? humanizeCode(value);
}

function normalizeScope(value) {
  const key = normalizeCode(value);
  const map = {
    national: 'National',
    state_specific: 'State Specific',
    statespecific: 'State Specific',
    lga_targeted: 'LGA Targeted',
    lgatargeted: 'LGA Targeted',
  };
  return map[key] ?? humanizeCode(value);
}

function normalizePlanCategory(value) {
  const key = normalizeCode(value);
  const map = {
    workplan: 'Workplan',
    reform_plan: 'Reform Plan',
    reformplan: 'Reform Plan',
  };
  return map[key] ?? humanizeCode(value);
}

function normalizeBeneficiaryClass(value) {
  const key = normalizeCode(value);
  const map = {
    farmer: 'Farmer',
    herder: 'Herder',
    processor: 'Processor',
    trader: 'Trader',
    cooperative: 'Cooperative',
    women: 'Women',
    youth: 'Youth',
    other: 'Other',
  };
  return map[key] ?? humanizeCode(value);
}

// Stored codes (e.g. "digital_transformation", "ict_m_e") → the option label.
function normalizeReformValue(value, options) {
  if (!value) return '';
  const key = normalizeCode(value);
  return options.find((option) => normalizeCode(option) === key) ?? humanizeCode(value);
}

function normalizeActivityType(value) {
  const key = normalizeCode(value);
  const map = {
    standard: 'Standard',
    reform: 'Reform',
    engagement: 'Engagement',
    coordination: 'Coordination',
    special_intervention: 'Special Intervention',
    ppp_milestone: 'PPP Milestone',
  };
  return map[key] ?? humanizeCode(value);
}

function normalizeWorkplan(row = {}) {
  const institutionObj = row.institution && typeof row.institution === 'object' ? row.institution : {};
  const ownerObj = row.plan_owner && typeof row.plan_owner === 'object' ? row.plan_owner : row.planOwner && typeof row.planOwner === 'object' ? row.planOwner : {};
  const title = firstText(row.title, row.plan_title, row.workplan_title);
  const year = firstText(row.year, row.period_year);
  const startDate = formatDateDisplay(firstText(row.start_date, row.startDate));
  const endDate = formatDateDisplay(firstText(row.end_date, row.endDate));

  return {
    ...row,
    uuid: row.uuid ?? row.id ?? null,
    id: row.uuid ?? row.id ?? null,
    institutionUuid: firstText(
      row.institution_uuid,
      row.institutionUuid,
      institutionObj.uuid,
      institutionObj.id,
      row.institution_id
    ),
    institution: firstText(
      row.institution_label,
      row.institutionLabel,
      row.institution_name,
      row.institutionName,
      row.institution?.name,
      institutionObj.name,
      institutionObj.label,
      row.institution_uuid,
      row.institutionUuid,
      row.institution_id
    ),
    title,
    planTitle: title,
    planType: normalizePlanType(firstText(row.plan_type, row.planType)),
    planCategory: normalizePlanCategory(firstText(row.plan_category, row.planCategory)),
    year,
    period: firstText(row.period, year, row.start_date, row.end_date),
    department: firstText(row.department, row.department_name, row.directorate),
    budgetNgn: parseMoney(row.budget_ngn ?? row.budget ?? row.committed_budget),
    budget: formatCurrencyCompact(row.budget_ngn ?? row.budget ?? row.committed_budget),
    planOwnerUuid: firstText(
      row.plan_owner_uuid,
      row.planOwnerUuid,
      ownerObj.uuid,
      ownerObj.id
    ),
    planOwner: firstText(
      row.plan_owner_name,
      row.planOwnerName,
      row.plan_owner?.name,
      row.plan_owner?.full_name,
      row.plan_owner?.label,
      row.planOwner,
      row.plan_owner_uuid,
      row.planOwnerUuid
    ),
    status: normalizeStatusLabel(firstText(row.workplan_status, row.status)),
    approvalStatus: normalizeStatusLabel(firstText(row.data_status, row.dataStatus, row.approval_status, row.approvalStatus)),
    approvingAuthority: normalizeStatusLabel(firstText(row.approving_authority_level, row.approvingAuthorityLevel)),
    geographicScope: normalizeScope(firstText(row.geographic_scope, row.geographicScope)),
    linkedProgramme: firstText(row.linked_programme, row.linkedProgramme, row.programme, row.programme_name),
    completionPercent: row.completion_percent ?? row.completionPct ?? row.completion_pct ?? null,
    reformCategory: normalizeReformValue(firstText(row.reform_category, row.reformCategory), REFORM_CATEGORIES),
    reformId: firstText(row.reform_id, row.reformId),
    strategicObjective: firstText(row.strategic_objective, row.strategicObjective),
    leadDepartment: normalizeReformValue(firstText(row.lead_department, row.leadDepartment), REFORM_LEAD_DEPARTMENTS),
    approvalAuthority: firstText(row.approval_authority, row.approvalAuthority),
    supportingDepartments: Array.isArray(row.supporting_departments ?? row.supportingDepartments)
      ? (row.supporting_departments ?? row.supportingDepartments)
      : (firstText(row.supporting_departments, row.supportingDepartments) ? String(firstText(row.supporting_departments, row.supportingDepartments)).split(',').map((item) => item.trim()).filter(Boolean) : []),
    startDate,
    endDate,
    state: firstText(row.state, row.state_name, row.stateName),
    dataStatus: firstText(row.data_status, row.dataStatus),
    data_status: firstText(row.data_status, row.dataStatus),
      submittedBy: personName(row.submitted_by) || personName(row.submittedBy) || null,
      submitted_by: row.submitted_by ?? row.submittedBy ?? null,
      submittedAt: row.submitted_at ?? row.submittedAt ?? null,
      submitted_at: row.submitted_at ?? row.submittedAt ?? null,
      validatedBy: personName(row.validated_by) || personName(row.validatedBy) || null,
      validated_by: row.validated_by ?? row.validatedBy ?? null,
      validatedAt: row.validated_at ?? row.validatedAt ?? null,
      validated_at: row.validated_at ?? row.validatedAt ?? null,
      approvedBy: personName(row.approved_by) || personName(row.approvedBy) || null,
      approved_by: row.approved_by ?? row.approvedBy ?? null,
    approvedAt: row.approved_at ?? row.approvedAt ?? null,
    approved_at: row.approved_at ?? row.approvedAt ?? null,
    rejectedAt: row.rejected_at ?? row.rejectedAt ?? null,
    rejected_at: row.rejected_at ?? row.rejectedAt ?? null,
    rejectionNotes: row.rejection_notes ?? row.rejectionNotes ?? null,
    rejection_notes: row.rejection_notes ?? row.rejectionNotes ?? null,
      createdBy: personName(row.created_by) || personName(row.createdBy) || null,
    created_by: row.created_by ?? row.createdBy ?? null,
    createdAt: row.created_at ?? row.createdAt ?? null,
    created_at: row.created_at ?? row.createdAt ?? null,
    updatedAt: row.updated_at ?? row.updatedAt ?? null,
    updated_at: row.updated_at ?? row.updatedAt ?? null,
    raw: row,
  };
}

function buildWorkplanPayload(payload = {}) {
  return {
    institution_uuid: trimOrNull(payload.institution_uuid ?? payload.institutionUuid),
    department: trimOrNull(payload.department),
    title: trimOrNull(payload.title ?? payload.plan_title ?? payload.planTitle),
    plan_title: trimOrNull(payload.plan_title ?? payload.planTitle ?? payload.title),
    plan_type: normalizeApiEnum(payload.plan_type ?? payload.planType),
    plan_category: normalizeApiEnum(payload.plan_category ?? payload.planCategory),
    year: trimOrNull(payload.year),
    start_date: trimOrNull(payload.start_date ?? payload.startDate),
    end_date: trimOrNull(payload.end_date ?? payload.endDate),
    budget_ngn: parseMoney(payload.budget_ngn ?? payload.budget),
    plan_owner_uuid: trimOrNull(payload.plan_owner_uuid ?? payload.planOwnerUuid),
    plan_owner: trimOrNull(payload.plan_owner ?? payload.planOwner),
    approving_authority_level: normalizeApiEnum(payload.approving_authority_level ?? payload.approvingAuthorityLevel),
    geographic_scope: normalizeApiEnum(payload.geographic_scope ?? payload.geographicScope),
    linked_programme: trimOrNull(payload.linked_programme ?? payload.linkedProgramme),
    workplan_status: normalizeApiEnum(payload.workplan_status ?? payload.status),
    status: normalizeApiEnum(payload.status),
    state: trimOrNull(payload.state),
    completion_percent: payload.completion_percent ?? payload.completionPercent ?? null,
    reform_category: normalizeApiEnum(payload.reform_category ?? payload.reformCategory),
    reform_id: trimOrNull(payload.reform_id ?? payload.reformId),
    strategic_objective: trimOrNull(payload.strategic_objective ?? payload.strategicObjective),
    lead_department: normalizeApiEnum(payload.lead_department ?? payload.leadDepartment),
    approval_authority: trimOrNull(payload.approval_authority ?? payload.approvalAuthority),
    supporting_departments: Array.isArray(payload.supporting_departments ?? payload.supportingDepartments)
      ? (payload.supporting_departments ?? payload.supportingDepartments)
      : (trimOrNull(payload.supporting_departments ?? payload.supportingDepartments) ? String(trimOrNull(payload.supporting_departments ?? payload.supportingDepartments)).split(',').map((item) => item.trim()).filter(Boolean) : undefined),
  };
}

function normalizeWorkplanActivity(row = {}) {
  const workplanObj = row.workplan && typeof row.workplan === 'object' ? row.workplan : {};

  return {
    ...row,
    uuid: row.uuid ?? row.id ?? null,
    id: row.uuid ?? row.id ?? null,
    workplanUuid: firstText(
      row.workplan_uuid,
      row.workplanUuid,
      workplanObj.uuid,
      workplanObj.id
    ),
    workplan: firstText(
      row.workplan_title,
      row.workplanTitle,
      row.workplan?.title,
      workplanObj.title,
      row.workplan_uuid,
      row.workplanUuid
    ),
    title: firstText(row.title, row.activity_title, row.activityTitle),
    activityTitle: firstText(row.activity_title, row.activityTitle, row.title),
    category: normalizeStatusLabel(firstText(row.category)),
    beneficiaryClass: normalizeBeneficiaryClass(firstText(row.beneficiary_class, row.beneficiaryClass)),
    climateScreening: firstText(row.climate_screening, row.climateScreening) || 'not_applicable',
    targetFacilityId: firstText(row.target_facility_id, row.targetFacilityId),
    state: firstText(row.state, row.state_name, row.stateName),
    lga: firstText(row.lga, row.lga_name, row.lgaName),
    startDate: formatDateDisplay(firstText(row.start_date, row.startDate)),
    endDate: formatDateDisplay(firstText(row.end_date, row.endDate)),
    budgetNgn: parseMoney(row.budget_ngn ?? row.budget),
    budget: formatCurrencyCompact(row.budget_ngn ?? row.budget),
    responsibleUnits: firstText(row.responsible_units, row.responsibleUnits, row.responsible_unit),
    status: normalizeStatusLabel(firstText(row.activity_status, row.activityStatus, row.status)),
    activityStatus: normalizeStatusLabel(firstText(row.activity_status, row.activityStatus, row.status)),
    actualExpenditureNgn: parseMoney(row.actual_expenditure_ngn ?? row.actualExpenditureNgN ?? row.actual_expenditure ?? row.actualExpenditure),
    actualExpenditure: formatCurrencyCompact(row.actual_expenditure_ngn ?? row.actualExpenditureNgN ?? row.actual_expenditure ?? row.actualExpenditure),
    completionPct: row.completion_percent ?? row.completionPct ?? row.completion_pct ?? null,
    targetBeneficiaries: row.target_beneficiaries ?? row.targetBeneficiaries ?? null,
    evidenceRef: firstText(
      row.evidence_output_reference,
      row.evidenceOutputReference,
      row.output_reference,
      row.outputReference,
      row.evidence_ref,
      row.evidenceRef
    ),
    dataStatus: firstText(row.data_status, row.dataStatus),
    data_status: firstText(row.data_status, row.dataStatus),
      submittedBy: personName(row.submitted_by) || personName(row.submittedBy) || null,
      submitted_by: row.submitted_by ?? row.submittedBy ?? null,
      submittedAt: row.submitted_at ?? row.submittedAt ?? null,
      submitted_at: row.submitted_at ?? row.submittedAt ?? null,
      validatedBy: personName(row.validated_by) || personName(row.validatedBy) || null,
      validated_by: row.validated_by ?? row.validatedBy ?? null,
      validatedAt: row.validated_at ?? row.validatedAt ?? null,
      validated_at: row.validated_at ?? row.validatedAt ?? null,
      approvedBy: personName(row.approved_by) || personName(row.approvedBy) || null,
      approved_by: row.approved_by ?? row.approvedBy ?? null,
    approvedAt: row.approved_at ?? row.approvedAt ?? null,
    approved_at: row.approved_at ?? row.approvedAt ?? null,
    rejectedAt: row.rejected_at ?? row.rejectedAt ?? null,
    rejected_at: row.rejected_at ?? row.rejectedAt ?? null,
    rejectionNotes: row.rejection_notes ?? row.rejectionNotes ?? null,
    rejection_notes: row.rejection_notes ?? row.rejectionNotes ?? null,
      createdBy: personName(row.created_by) || personName(row.createdBy) || null,
    created_by: row.created_by ?? row.createdBy ?? null,
    createdAt: row.created_at ?? row.createdAt ?? null,
    created_at: row.created_at ?? row.createdAt ?? null,
    updatedAt: row.updated_at ?? row.updatedAt ?? null,
    updated_at: row.updated_at ?? row.updatedAt ?? null,
    raw: row,
  };
}

function buildWorkplanActivityPayload(payload = {}) {
  return {
    workplan_uuid: trimOrNull(payload.workplan_uuid ?? payload.workplanUuid),
    title: trimOrNull(payload.title ?? payload.activity_title ?? payload.activityTitle),
    activity_title: trimOrNull(payload.activity_title ?? payload.activityTitle ?? payload.title),
    category: normalizeApiEnum(payload.category),
    beneficiary_class: normalizeApiEnum(payload.beneficiary_class ?? payload.beneficiaryClass),
    climate_screening: (payload.climate_screening ?? payload.climateScreening) === 'required' ? 'required' : 'not_applicable',
    target_facility_id: (payload.climate_screening ?? payload.climateScreening) === 'required'
      ? trimOrNull(payload.target_facility_id ?? payload.targetFacilityId)
      : null,
    start_date: trimOrNull(payload.start_date ?? payload.startDate),
    end_date: trimOrNull(payload.end_date ?? payload.endDate),
    budget_ngn: parseMoney(payload.budget_ngn ?? payload.budget),
    target_beneficiaries: payload.target_beneficiaries ?? payload.targetBeneficiaries ?? null,
    activity_status: normalizeApiEnum(payload.activity_status ?? payload.activityStatus ?? payload.status),
    status: normalizeApiEnum(payload.status),
  };
}

function normalizeActivityReport(row = {}) {
  const activityObj = row.workplan_activity && typeof row.workplan_activity === 'object' ? row.workplan_activity : row.workplanActivity && typeof row.workplanActivity === 'object' ? row.workplanActivity : {};
  const outputsTarget = row.outputs_target ?? row.outputsTarget ?? null;
  const outputsAchieved = row.outputs_achieved ?? row.outputsAchieved ?? null;

  return {
    ...row,
    uuid: row.uuid ?? row.id ?? null,
    id: row.uuid ?? row.id ?? null,
    activityUuid: firstText(
      row.workplan_activity_uuid,
      row.workplanActivityUuid,
      activityObj.uuid,
      activityObj.id
    ),
    activityId: firstText(
      row.workplan_activity_title,
      row.workplanActivityTitle,
      row.workplan_activity?.title,
      activityObj.title,
      row.workplan_activity_uuid,
      row.workplanActivityUuid
    ),
    activityType: normalizeActivityType(firstText(row.activity_type, row.activityType)),
    pppId: firstText(row.ppp_id, row.pppId),
    reformPlanUuid: firstText(row.reform_plan_uuid, row.reformPlanUuid),
    reformCurrentStatus: normalizeReformValue(firstText(row.reform_current_status, row.reformCurrentStatus), REFORM_MONITORING_STATUSES),
    completedActivityUuids: Array.isArray(row.completed_activity_uuids ?? row.completedActivityUuids)
      ? (row.completed_activity_uuids ?? row.completedActivityUuids)
      : [],
    remarks: firstText(row.remarks),
    reportingPeriod: firstText(row.reporting_period, row.reportingPeriod),
    actualStartDate: formatDateDisplay(firstText(row.actual_start_date, row.actualStartDate, row.start_date, row.startDate)),
    startDate: formatDateDisplay(firstText(row.actual_start_date, row.actualStartDate, row.start_date, row.startDate)),
    executionStatus: normalizeStatusLabel(firstText(row.execution_status, row.executionStatus, row.status)),
    actualExpenditureNgn: parseMoney(row.actual_expenditure_ngn ?? row.actualExpenditureNgn ?? row.expenditure_ngn ?? row.expenditure),
    expenditureNgn: parseMoney(row.expenditure_ngn ?? row.expenditure ?? row.actual_expenditure_ngn),
    expenditure: formatCurrencyCompact(row.expenditure_ngn ?? row.expenditure ?? row.actual_expenditure_ngn),
    outputsTarget: summarizeStructuredValue(outputsTarget),
    outputsTargetData: outputsTarget,
    outputsAchieved: summarizeStructuredValue(outputsAchieved),
    outputsAchievedData: outputsAchieved,
    outputsCount: sumNumericValues(outputsAchieved),
    attachments: row.attachments ?? row.attachment ?? row.evidence_attachment ?? row.evidenceAttachment ?? '',
    attachmentsFileName: row.attachments_file_name ?? row.attachmentsFileName ?? '',
    facilityAssessmentRef: firstText(row.facility_assessment_ref, row.facilityAssessmentRef),
    varianceExpl: firstText(
      row.variance_explanation,
      row.varianceExplanation,
      row.comments,
      row.note
    ),
    reportedByUuid: firstText(row.reported_by_uuid, row.reportedByUuid),
    reportedBy: firstText(row.reported_by_name, row.reportedByName, row.reported_by, row.reportedBy, row.reported_by_uuid, row.reportedByUuid),
    validatedByUuid: firstText(row.validated_by_uuid, row.validatedByUuid),
    validatedBy: personName(row.validated_by_name) || personName(row.validatedByName) || personName(row.validated_by) || personName(row.validatedBy) || personName(row.validated_by_uuid) || personName(row.validatedByUuid) || null,
    submissionDate: formatDateDisplay(firstText(row.submission_date, row.submissionDate)),
    state: firstText(row.state, row.state_name, row.stateName),
    lga: firstText(row.lga, row.lga_name, row.lgaName),
    dataStatus: firstText(row.data_status, row.dataStatus),
    data_status: firstText(row.data_status, row.dataStatus),
      submittedBy: personName(row.submitted_by) || personName(row.submittedBy) || null,
      submitted_by: row.submitted_by ?? row.submittedBy ?? null,
      submittedAt: row.submitted_at ?? row.submittedAt ?? null,
      submitted_at: row.submitted_at ?? row.submittedAt ?? null,
      validated_by: row.validated_by ?? row.validatedBy ?? null,
      validatedAt: row.validated_at ?? row.validatedAt ?? null,
      validated_at: row.validated_at ?? row.validatedAt ?? null,
      approvedBy: personName(row.approved_by) || personName(row.approvedBy) || null,
      approved_by: row.approved_by ?? row.approvedBy ?? null,
    approvedAt: row.approved_at ?? row.approvedAt ?? null,
    approved_at: row.approved_at ?? row.approvedAt ?? null,
    rejectedAt: row.rejected_at ?? row.rejectedAt ?? null,
    rejected_at: row.rejected_at ?? row.rejectedAt ?? null,
    rejectionNotes: row.rejection_notes ?? row.rejectionNotes ?? null,
    rejection_notes: row.rejection_notes ?? row.rejectionNotes ?? null,
      createdBy: personName(row.created_by) || personName(row.createdBy) || null,
    created_by: row.created_by ?? row.createdBy ?? null,
    createdAt: row.created_at ?? row.createdAt ?? null,
    created_at: row.created_at ?? row.createdAt ?? null,
    updatedAt: row.updated_at ?? row.updatedAt ?? null,
    updated_at: row.updated_at ?? row.updatedAt ?? null,
    raw: row,
  };
}

function buildActivityReportPayload(payload = {}) {
  return {
    workplan_activity_uuid: trimOrNull(payload.workplan_activity_uuid ?? payload.workplanActivityUuid ?? payload.parent_activity_id ?? payload.parentActivityId),
    parent_activity_id: trimOrNull(payload.parent_activity_id ?? payload.parentActivityId ?? payload.workplan_activity_uuid ?? payload.workplanActivityUuid),
    activity_type: normalizeApiEnum(payload.activity_type ?? payload.activityType),
    ppp_id: trimOrNull(payload.ppp_id ?? payload.pppId),
    reform_plan_uuid: trimOrNull(payload.reform_plan_uuid ?? payload.reformPlanUuid),
    reform_current_status: normalizeApiEnum(payload.reform_current_status ?? payload.reformCurrentStatus),
    completed_activity_uuids: Array.isArray(payload.completed_activity_uuids ?? payload.completedActivityUuids)
      ? (payload.completed_activity_uuids ?? payload.completedActivityUuids)
      : undefined,
    remarks: trimOrNull(payload.remarks),
    reporting_period: trimOrNull(payload.reporting_period ?? payload.reportingPeriod),
    actual_start_date: trimOrNull(payload.actual_start_date ?? payload.actualStartDate),
    execution_status: normalizeApiEnum(payload.execution_status ?? payload.executionStatus ?? payload.status),
    expenditure_ngn: parseMoney(payload.expenditure_ngn ?? payload.expenditure),
    actual_expenditure_ngn: parseMoney(payload.actual_expenditure_ngn ?? payload.actualExpenditureNgn ?? payload.expenditure_ngn ?? payload.expenditure),
    outputs_target: parseStructuredField(payload.outputs_target ?? payload.outputsTarget),
    outputs_achieved: parseStructuredField(payload.outputs_achieved ?? payload.outputsAchieved),
    submission_date: trimOrNull(payload.submission_date ?? payload.submissionDate),
    reported_by_uuid: trimOrNull(payload.reported_by_uuid ?? payload.reportedByUuid),
    variance_explanation: trimOrNull(payload.variance_explanation ?? payload.varianceExplanation ?? payload.varianceExpl),
    attachments: payload.attachments ?? payload.attachment ?? payload.evidence_attachment ?? payload.evidenceAttachment ?? undefined,
    attachments_file_name: payload.attachments_file_name ?? payload.attachmentsFileName ?? undefined,
    facility_assessment_ref: trimOrNull(payload.facility_assessment_ref ?? payload.facilityAssessmentRef),
  };
}

function archiveDeletedRecord(cacheKey, record = {}, response = null) {
  if (!record || typeof record !== 'object') return null;

  const timestamp = new Date().toISOString();
  const archived = {
    ...record,
    deleted_at: timestamp,
    deletedAt: timestamp,
    delete_response: response ?? null,
    deleteResponse: response ?? null,
  };

  const current = readRegistryCollection(cacheKey) ?? [];
  writeRegistryCollection(cacheKey, [archived, ...current]);
  return archived;
}

export function archiveDeletedWorkplan(record = {}, response = null) {
  return archiveDeletedRecord(DELETED_WORKPLANS_KEY, record, response);
}

export function archiveDeletedWorkplanActivity(record = {}, response = null) {
  return archiveDeletedRecord(DELETED_WORKPLAN_ACTIVITIES_KEY, record, response);
}

export function archiveDeletedActivityReport(record = {}, response = null) {
  return archiveDeletedRecord(DELETED_ACTIVITY_REPORTS_KEY, record, response);
}

const normalizeWorkplanRecord = withExtensions('workplans', normalizeWorkplan);
const normalizeWorkplanActivityRecord = withExtensions('workplan-activities', normalizeWorkplanActivity);
const normalizeActivityReportRecord = withExtensions('activity-reports', normalizeActivityReport);

export const workplansApi = withOfflineCollectionSupport(makeCollectionApi({
  basePath: '/v1/workplans',
  normalize: normalizeWorkplanRecord,
  buildPayload: buildWorkplanPayload,
  defaultPerPage: 25,
  itemKeys: ['title', 'plan_type', 'year'],
  collectionKey: 'workplans',
}), {
  basePath: '/v1/workplans',
  collectionKey: 'workplans',
  normalize: normalizeWorkplanRecord,
  buildPayload: buildWorkplanPayload,
});

export const workplanActivitiesApi = withOfflineCollectionSupport(makeCollectionApi({
  basePath: '/v1/workplan-activities',
  normalize: normalizeWorkplanActivityRecord,
  buildPayload: buildWorkplanActivityPayload,
  defaultPerPage: 25,
  itemKeys: ['workplan_uuid', 'title', 'category'],
  collectionKey: 'workplan-activities',
}), {
  basePath: '/v1/workplan-activities',
  collectionKey: 'workplan-activities',
  normalize: normalizeWorkplanActivityRecord,
  buildPayload: buildWorkplanActivityPayload,
});

export const activityReportsApi = withOfflineCollectionSupport(makeCollectionApi({
  basePath: '/v1/activity-reports',
  normalize: normalizeActivityReportRecord,
  buildPayload: buildActivityReportPayload,
  defaultPerPage: 25,
  itemKeys: ['workplan_activity_uuid', 'reporting_period', 'execution_status'],
  collectionKey: 'activity-reports',
}), {
  basePath: '/v1/activity-reports',
  collectionKey: 'activity-reports',
  normalize: normalizeActivityReportRecord,
  buildPayload: buildActivityReportPayload,
});

export {
  buildActivityReportPayload,
  buildQueryParams,
  buildWorkplanActivityPayload,
  buildWorkplanPayload,
  cleanQueryValue,
  extractCollection,
  extractItem,
  formatCurrencyCompact,
  formatDateDisplay,
  formatDateInput,
  humanizeCode,
  makeCollectionApi,
  normalizeActivityReport,
  normalizeCode,
  normalizeDateValue,
  normalizePlanType,
  normalizeScope,
  normalizeStatusLabel,
  normalizeWorkplan,
  normalizeWorkplanActivity,
  parseMoney,
  parseStructuredField,
  summarizeStructuredValue,
  sumNumericValues,
  trimOrNull,
  unwrapResponse,
};
